import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GlobalDeleteModal from "../../components/partials/GlobalDeleteModal";
import CategoryAPI from "../../api/categoryApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { formatDate } from "../../helper/Helper";
import { useLoader } from "../../utils/LoaderUtils";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import categoryAPI from "../../api/categoryApi";
import TableShimmer from "../../components/partials/TableShimmer";
import useQueryFilters from "../../hooks/useQueryFilters";
import useDeleteItem from "../../hooks/useDeleteItem";
import SortableRow from "./SortableRow";

function List() {
  const { showLoader, hideLoader } = useLoader();
  const [tableLoading, setTableLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState({});

  const toggleCollapse = (id) => {
    setCollapsed((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  /* =============  FETCH API DATA ================ */
  const { query, updateFilter, handleSearch, searchParams, searchMessage } =
    useQueryFilters();

  const fetchCategoryData = useCallback(async () => {
    setTableLoading(true);
    setItems([]);

    try {
      const res = await categoryAPI.getDetails(searchParams);

      if (res.code === 200) {
        setItems(res.data);

        const collapsedState = {};
        const markCollapsed = (list) => {
          list.forEach((item) => {
            if (item.children && item.children.length > 0) {
              collapsedState[item._id] = true; // true = collapsed
              markCollapsed(item.children);
            }
          });
        };
        markCollapsed(res.data);
        setCollapsed(collapsedState);
      }
    } catch (err) {
    } finally {
      setTableLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchCategoryData();
  }, [fetchCategoryData]);

  const { isDeleting, openDelete, handleDelete } = useDeleteItem(
    categoryAPI.deleteItem,
    fetchCategoryData,
  );

  /* =============  STATUS CHANGE API ================ */
  const handleStatusChange = useCallback(async (id, status) => {
    showLoader();

    try {
      const res = await categoryAPI.updateActiveStatus(id);

      if (res.code === 200) {
        hideLoader();
        showSuccess("Status updated successfully");

        setItems((prev) =>
          prev.map((item) => (item._id === id ? { ...item, status } : item)),
        );
      } else {
        showError("Failed to update status");
      }
    } catch (err) {
      hideLoader();
      showError("Something went wrong");
    }
  });

  // update item
  const updateItem = (items, id, updater) => {
    return items.map((item) => {
      if (item._id === id) {
        return updater(item);
      }

      if (item.children) {
        return {
          ...item,
          children: updateItem(item.children, id, updater),
        };
      }

      return item;
    });
  };

  const handleStatus = useCallback(async (type, id, status) => {
    showLoader();

    try {
      const payload = {
        status: status,
        type: type,
      };

      const res = await CategoryAPI.updateStatus(id, payload);

      if (res.code === 200) {
        showSuccess("Status updated successfully");
        hideLoader();

        setItems((prev) =>
          updateItem(prev, id, (item) => ({
            ...item,
            [type === "navbar" ? "showInNavbar" : "showOnHomepage"]: status,
          })),
        );
      } else {
        showError("Failed to update status");
      }
    } catch (err) {
      hideLoader();
      showError("Something went wrong");
    }
  });

  const flattenVisibleItems = (list, collapsedMap) => {
    let result = [];

    list.forEach((item) => {
      result.push(item._id);

      if (!collapsedMap[item._id] && item.children?.length) {
        result = result.concat(
          flattenVisibleItems(item.children, collapsedMap),
        );
      }
    });

    return result;
  };

  const findContainerAndIndex = (items, id) => {
    for (let i = 0; i < items.length; i++) {
      if (items[i]._id === id) {
        return { container: items, index: i, parent: null };
      }
      if (items[i].children && items[i].children.length > 0) {
        const childResult = findContainerAndIndex(items[i].children, id);
        if (childResult) {
          return { ...childResult, parent: items[i] };
        }
      }
    }
    return null;
  };

  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      // Active aur Over items ko nested tree mein dhoondhein
      const activeInfo = findContainerAndIndex(items, active.id);
      const overInfo = findContainerAndIndex(items, over.id);

      // Reorder tabhi allowed hai jab dono items same parent/level ke andar hon
      if (
        !activeInfo ||
        !overInfo ||
        activeInfo.container !== overInfo.container
      ) {
        showError("Simbly same level/category ke andar reorder kar sakte hain");
        return;
      }

      const { container, index: oldIndex } = activeInfo;
      const { index: newIndex } = overInfo;

      // Array ko move karein
      const reorderedSublist = arrayMove(container, oldIndex, newIndex);

      // Deep copy update state
      const updateNestedItems = (list) => {
        if (list === container) return reorderedSublist;

        return list.map((item) => {
          if (item.children && item.children.length > 0) {
            return {
              ...item,
              children: updateNestedItems(item.children),
            };
          }
          return item;
        });
      };

      const newItems = updateNestedItems(items);
      setItems(newItems);

      // API Payload Prepare karein
      const updatedOrder = reorderedSublist.map((item, index) => ({
        id: item._id,
        sort_order: index + 1,
        parentId: activeInfo.parent ? activeInfo.parent._id : null,
      }));

      try {
        const res = await categoryAPI.updateOrder(updatedOrder);
        if (res.code !== 200) {
          showError(res.message || "Failed to update order");
          fetchCategoryData();
        } else {
          showSuccess("Order updated successfully");
        }
      } catch (error) {
        console.error("Order update failed", error);
        showError("Order update failed");
        fetchCategoryData();
      }
    },
    [items, fetchCategoryData],
  );

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Manage Store Categories</h4>
              <h6>
                Create, organize, and customize product categories and
                subcategories to structure your store effectively.
              </h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/category/add-new" className="btn btn-primary">
              <i className="ti ti-circle-plus me-1" />
              Add Category
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text" id="basic-addon1">
                    <i className="ti ti-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search By Store Name/Email Address/Phone Number"
                    defaultValue={query.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>

                {searchMessage && (
                  <small className="text-danger">{searchMessage}</small>
                )}
              </div>
              <div className="col-md-6" style={{ justifyItems: "right" }}>
                <div className="d-flex table-dropdown my-xl-auto right-content align-items-center row-gap-3">
                  <select
                    className="form-control ms-2"
                    value={searchParams.get("status") || ""}
                    onChange={(e) => updateFilter("status", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="row">
              <div className="col-md-12">
                <div className="countablepagination p-2">
                  <p>Total: {items?.length} results</p>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="table w-100">
                  <thead className="thead-light">
                    <tr>
                      <th>S. No.</th>
                      <th>Category</th>
                      <th>Created At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading ? (
                      <TableShimmer rows={10} cols={4} />
                    ) : items.length > 0 ? (
                      <SortableContext
                        items={flattenVisibleItems(items, collapsed)}
                        strategy={verticalListSortingStrategy}
                      >
                        {items.map((item, index) => (
                          <SortableRow
                            key={item._id}
                            item={item}
                            index={index}
                            level={0}
                            collapsed={collapsed}
                            toggleCollapse={toggleCollapse}
                            handleStatusChange={handleStatusChange}
                            handleStatus={handleStatus}
                            openDelete={openDelete}
                          />
                        ))}
                      </SortableContext>
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center p-4 text-muted">
                          No category Added,{" "}
                          <Link to="/category/add-new" className="text-primary">
                            Click Here{" "}
                          </Link>
                          to add new category
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </DndContext>
            </div>
          </div>
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default List;
